import { useMemo } from "react";
import { useBuild } from "app/components/build/Show/lib/BuildContext";
import FriendlyTime from "app/components/shared/FriendlyTime";
import Icon from "app/components/shared/Icon";
import { useNavigate } from "react-router-dom";
import { getDurationString, getDuration } from "app/lib/date";
import BktecBadge from "app/components/shared/BktecBadge";
import { BktecLogo } from "app/components/shared/BktecLogo/BktecLogo";
import { urlForView } from "../../lib/urlForView";
import { BuildView } from "../../lib/types/BuildView";
import {
  FilterByOption,
  useFilterStore,
  useStateCounts,
} from "../../components/Sidebar/useFilterStore";
import Duration from "app/components/shared/Duration";

// Map build states to display text
const buildStateDisplayMap: { [key: string]: string } = {
  scheduled: "scheduled",
  blocked: "blocked",
  running: "running",
  finished: "finished",
  failed: "failed",
  canceled: "canceled",
  canceling: "canceling",
  not_run: "not run",
  started: "running",
};

export default function ExecutionDetailsCard() {
  const { build } = useBuild();
  const setFilterBy = useFilterStore((state) => state.setFilterBy);
  const navigate = useNavigate();
  const counts = useStateCounts();
  const failedCount = counts["failed"];

  const handleOpenFailedSteps = () => {
    setFilterBy([FilterByOption.Failed]);
    navigate(urlForView(build.path, BuildView.Steps));
  };

  const handleOpenAllSteps = () => {
    setFilterBy([]);
    navigate(urlForView(build.path, BuildView.Canvas));
  };

  const handleOpenAllJobs = () => {
    setFilterBy([]);
    navigate(urlForView(build.path, BuildView.Steps));
  };

  let queueDurationStr: string | null = null;

  if (build.scheduledAt && build.startedAt) {
    const durationSeconds = getDuration(build.scheduledAt, build.startedAt).asSeconds();
    if (durationSeconds >= 0) {
      queueDurationStr = getDurationString(durationSeconds, "short");
    }
  }

  // --- Determine Queue Info ---
  const uniqueQueueKeys = useMemo(() => {
    return build.jobs
      ?.map((job) => job.clusterQueue?.key)
      .filter((key): key is string => !!key) // Filter out null/undefined keys and ensure type is string
      .reduce((acc, key) => {
        if (!acc.includes(key)) {
          acc.push(key);
        }
        return acc;
      }, [] as string[]);
  }, [build.jobs]);

  let queueDisplay: React.ReactNode = null;
  if (uniqueQueueKeys && uniqueQueueKeys.length === 1) {
    queueDisplay = <div className="text-xs sm:text-sm truncate">{uniqueQueueKeys[0]}</div>;
  } else if (uniqueQueueKeys && uniqueQueueKeys.length > 1) {
    queueDisplay = <div className="text-xs sm:text-sm">Multiple queues</div>;
  }
  // --- End Queue Info ---

  // --- Determine Agent Type Info ---
  const scriptJobs = build.jobs?.filter((job) => job.type === "script");
  const hasHosted = scriptJobs?.some((job) => job.hosted);
  const hasSelfHosted = scriptJobs?.some((job) => !job.hosted);

  let agentTypeDisplay: React.ReactNode = null;
  let agentTypeIcon: string | null = null;

  if (hasHosted && hasSelfHosted) {
    agentTypeDisplay = <div className="text-xs sm:text-sm">Mixed</div>;
    agentTypeIcon = "custom/outline/14x/agent"; // Generic icon for mixed
  } else if (hasHosted) {
    agentTypeDisplay = <div className="text-xs sm:text-sm">Hosted</div>;
    agentTypeIcon = "heroicons/outline/cloud";
  } else if (hasSelfHosted) {
    agentTypeDisplay = <div className="text-xs sm:text-sm">Self-hosted</div>;
    agentTypeIcon = "heroicons/outline/server-stack";
  }
  // --- End Agent Type Info ---

  const totalSteps = useMemo(() => {
    return build.steps?.filter((step) => step.type !== "group").length;
  }, [build.steps?.length]);

  // Calculate total number of jobs run (script or trigger, not broken)
  const totalStartedJobs = useMemo(() => {
    const startedStates = ["running", "canceling", "finished", "failed", "canceled"];
    return build.jobs?.filter(
      (job) =>
        job.state !== "broken" &&
        (job.type === "script" || job.type === "trigger") &&
        startedStates.includes(job.state),
    ).length;
  }, [build.jobs]);

  return (
    <div
      className="flex flex-col h-full rounded-md shadow-depth-200 bg-white p-2 sm:p-4"
      data-testid="build-details-card"
    >
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-base sm:text-lg font-semibold my-0">Execution details</h2>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-3 mb-3 text-xs sm:text-sm">
        <div className="min-w-[calc(50%-8px)]">
          <span className="text-gray-900 block mb-0.5">Status</span>
          <div className="flex items-center gap-1">
            <div className="text-xs sm:text-sm capitalize">
              {buildStateDisplayMap[build.state] || build.state}
            </div>
          </div>
        </div>

        {build.finishedAt && (
          <div className="min-w-[calc(50%-8px)]">
            <span className="text-gray-900 block mb-0.5">
              {build.state === "canceled" || build.state === "canceling" ? "Cancelled" : "Finished"}
            </span>
            <div className="flex items-center gap-1">
              <div className="text-xs sm:text-sm">
                <FriendlyTime value={build.finishedAt} capitalized={true} />
              </div>
            </div>
          </div>
        )}

        {/* Execution & Queued time */}
        {build.state !== "canceled" && build.state !== "canceling" && build.state !== "not_run" && (
          <div className="min-w-[calc(50%-8px)]">
            <span className="text-gray-900 block mb-0.5">Execution time</span>
            <div className="text-xs sm:text-sm flex items-center gap-1">
              {/* Show Waiting if scheduled/blocked and not started, otherwise show duration/running */}
              {build.startedAt ? (
                // Only pass 'to' if build is finished, otherwise omit the prop
                build.finishedAt ? (
                  <Duration.Short
                    from={build.startedAt}
                    to={build.finishedAt}
                    updateFrequency={1000}
                  />
                ) : (
                  <Duration.Short from={build.startedAt} updateFrequency={1000} />
                )
              ) : build.state === "scheduled" || build.state === "blocked" ? (
                // Build has not started and is scheduled/blocked
                build.scheduledAt ? (
                  <>
                    Waiting <Duration.Short from={build.scheduledAt} updateFrequency={1000} />
                  </>
                ) : (
                  <>Waiting</> // Fallback in case scheduledAt is not available
                )
              ) : null}
            </div>
          </div>
        )}

        {queueDurationStr && (
          <div className="min-w-[calc(50%-8px)]">
            <span className="text-gray-900 block mb-0.5">Queued time</span>
            <div className="text-xs sm:text-sm">{queueDurationStr}</div>
          </div>
        )}

        {/* Divider */}
        <div className="w-full border-b" />

        {/* Combined Steps/Failed Steps and Jobs run */}
        <div className="flex flex-wrap gap-x-4 gap-y-3 w-full">
          <div className="min-w-[calc(50%-8px)]">
            <span className="text-gray-900 block mb-0.5">Steps</span>
            <div className="text-xs sm:text-sm flex items-center gap-1">
              <button
                onClick={handleOpenAllSteps}
                className="text-xs sm:text-sm text-charcoal-700 hover:text-charcoal-900 font-medium inline-flex hover:underline p-0 border-none bg-transparent"
              >
                {totalSteps ?? "N/A"}
              </button>
              {failedCount > 0 && (
                <div className="group flex items-center gap-0.5 bg-red-100 rounded-md px-2">
                  <button
                    onClick={handleOpenFailedSteps}
                    className="text-xs sm:text-sm flex items-center gap-0 text-red-600 group-hover:text-red-800 font-medium inline-flex group-hover:underline p-0 border-none bg-transparent"
                  >
                    {failedCount} Failed
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="min-w-[calc(50%-8px)]">
            <span className="text-gray-900 block mb-0.5">Jobs run</span>
            <button
              onClick={handleOpenAllJobs}
              className="text-xs sm:text-sm text-charcoal-700 hover:text-charcoal-900 font-medium inline-flex hover:underline p-0 border-none bg-transparent"
            >
              {totalStartedJobs ?? "N/A"}
            </button>
          </div>
        </div>

        {/* Retries, Agent Type, and Queue */}
        <div className="flex flex-wrap gap-x-4 gap-y-3 w-full">
          {build.retry_counts && build.retry_counts.total > 0 && (
            <div className="min-w-[calc(50%-8px)]">
              <span className="text-gray-900 block mb-0.5">Retries</span>
              <div className="text-xs sm:text-sm">
                {build.retry_counts.total}
                <span className="text-xs text-gray-500 ml-1 font-normal">
                  ({build.retry_counts.manual} Manual, {build.retry_counts.automatic} Automatic)
                </span>
              </div>
            </div>
          )}
          {/* Environment Details */}
          {agentTypeDisplay && agentTypeIcon && (
            <div className="min-w-[calc(50%-8px)]">
              <span className="text-gray-900 block mb-0.5">Agent type</span>
              <div className="text-xs sm:text-sm flex items-center gap-1">
                <Icon icon={agentTypeIcon} className="h-4 w-4 !m-0 text-gray-900" />
                {agentTypeDisplay}
              </div>
            </div>
          )}
          {queueDisplay && (
            <div className="min-w-[calc(50%-8px)]">
              <span className="text-gray-900 block mb-0.5">
                Queue{uniqueQueueKeys?.length > 1 ? "s" : ""}
              </span>
              <div className="text-xs sm:text-sm flex items-center gap-1">
                <Icon icon="custom/outline/14x/queue" className="h-4 w-4 !m-0 text-gray-900" />
                {queueDisplay}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* BK Tech Badge section */}
      <div className="border-t pt-3 mt-3">
        {build.project?.bktecBadgeEnabled && build.steps ? (
          <BktecBadge
            buildId={build.id}
            pipelineHasHistoricalTestPlans={build.project.hasTestPlans}
            steps={build.steps}
          />
        ) : (
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div className="flex items-center gap-2 flex-wrap text-xs sm:text-sm text-gray-900 pointer-events-none">
              <BktecLogo state="unknown" />
              Not enabled
            </div>
            <button
              onClick={() => {
                window.open(
                  "https://buildkite.com/docs/test-engine/test-state-and-quarantine#configuring-builds-with-quarantine-bktec",
                  "_blank",
                );
              }}
              className="bg-white border border-slate-400 shadow-sm rounded px-1.5 py-0.5 text-charcoal-700 transition-colors no-underline hover:bg-gray-100 hover:text-charcoal-600 active:text-charcoal-600 focus:text-charcoal-600 text-xs sm:text-sm flex items-center gap-1"
            >
              Learn More
              <Icon icon="heroicons/16/solid/arrow-top-right-on-square" className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
